import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import AuthShell from '../components/auth/AuthShell';
import AuthField from '../components/auth/AuthField';
import AuthAlert from '../components/auth/AuthAlert';
import styles from '../components/auth/authUi.module.css';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showBackButton, setShowBackButton] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const fromSettingsPage = location.state?.fromSettingsPage;
    // `fromAccountSettings` is the older key, kept so a link that predates the
    // account hub still shows a back button rather than stranding the reader.
    const fromAccount = location.state?.fromAccount || location.state?.fromAccountSettings;
    const fromLoginPage = location.state?.fromLoginPage;

    if ((fromSettingsPage && token) || fromAccount || fromLoginPage) {
      setShowBackButton(true);
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    try {
      const response = await api.post('/api/auth/forgot-password', { email });
      setMessage(response.data.message);
    } catch (error) {
      console.error('Forgot password error:', error.response ? error.response.data : error.message);
      
      // Handle network errors
      if (!error.response) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
        return;
      }
      
      // Handle specific error cases with user-friendly messages
      const errorMessage = error.response?.data?.error || 'An unexpected error occurred. Please try again.';
      const statusCode = error.response?.status;
      
      if (statusCode === 500) {
        setError('Our service is temporarily unavailable. Please try again in a few moments.');
      } else if (errorMessage) {
        // Use the error message directly if it's already user-friendly
        setError(errorMessage);
      } else {
        setError('Unable to send reset link. Please try again.');
      }
    }
  };

  const cameFromAccount = Boolean(
    location.state?.fromAccount || location.state?.fromAccountSettings || location.state?.fromSettingsPage
  );

  const handleBackClick = () => {
    setIsNavigating(true);
    if (cameFromAccount) {
      navigate('/account');
    } else if (location.state?.fromLoginPage) {
      navigate('/login');
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter the email on your account and we'll send you a link to set a new password."
      footer={
        cameFromAccount ? null : (
          <>
            Remembered it? <Link to="/login">Back to sign in</Link>
          </>
        )
      }
      aside={
        // The app-wide back control: fixed to the bottom of the viewport, and
        // styled by Global.css so it matches every other page that has one.
        showBackButton && !isNavigating ? (
          <button
            className={cameFromAccount ? 'back-to-manage-account-button' : 'back-to-contacts-button'}
            onClick={handleBackClick}
          >
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {cameFromAccount && <span>Account</span>}
          </button>
        ) : null
      }
    >
      <AuthAlert tone="success">{message}</AuthAlert>
      <AuthAlert>{error}</AuthAlert>

      <form className={styles.form} onSubmit={handleSubmit}>
        <AuthField
          id="forgot-email"
          name="email"
          label="Email"
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="username email"
          autoFocus
          spellCheck="false"
          autoCorrect="off"
          autoCapitalize="off"
          inputMode="email"
        />

        <button type="submit" className={styles.submit}>
          Send reset link
        </button>
      </form>
    </AuthShell>
  );
};

export default ForgotPasswordPage;
